import { Fragment, useMemo, type ReactNode } from 'react';
import { XIcon } from 'lucide-react';

import { FLOATING_OVERLAY_DEFAULT_INSET_PX } from '@/shared/config';
import { Button } from '@/shared/ui/shadcn/button';
import {
  FloatingOverlayPortal,
  OverlayPanel,
  type Position,
  type Size,
} from '@/shared/ui/primitives';
import type { CopilotOverlayEntry, CopilotOverlayId } from '@/shared/stores/ui';

import { COPILOT_OVERLAY_DEFINITIONS } from './model/overlayDefinitions';

export interface CopilotOverlayOutletProps {
  overlays: CopilotOverlayEntry[];
  userId: string;
  onClose: (id?: CopilotOverlayId) => void;
}

function getBottomRightPosition(size: Size, insetPx: number): Position {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const widthPx = typeof size.width === 'number' ? size.width : 0;
  const heightPx = typeof size.height === 'number' ? size.height : 0;

  return {
    x: Math.max(insetPx, window.innerWidth - widthPx - insetPx),
    y: Math.max(insetPx, window.innerHeight - heightPx - insetPx),
  };
}

export function CopilotOverlayOutlet({ overlays, userId, onClose }: CopilotOverlayOutletProps) {
  const defaultPositions = useMemo(() => {
    if (overlays.length === 0) {
      return [];
    }
    let nextX: number | undefined;

    return overlays.map((overlay) => {
      const definition = COPILOT_OVERLAY_DEFINITIONS[overlay.id];
      const base = getBottomRightPosition(
        definition.defaultSize,
        FLOATING_OVERLAY_DEFAULT_INSET_PX
      );
      const x = nextX ?? base.x;
      nextX =
        x -
        (typeof definition.defaultSize.width === 'number' ? definition.defaultSize.width : 0) -
        16;
      return { x, y: base.y };
    });
  }, [overlays]);

  if (overlays.length === 0 || defaultPositions.length === 0) {
    return null;
  }

  return (
    <FloatingOverlayPortal>
      {overlays.map((overlay, index) => {
        const definition = COPILOT_OVERLAY_DEFINITIONS[overlay.id];
        // The store carries ``payload`` opaquely; the registry knows the
        // concrete type per id. A single cast at this dispatch boundary is the
        // standard trade-off for a heterogeneous id→component registry.
        const render = definition.render as (userId: string, payload: unknown) => ReactNode;
        return (
          <OverlayPanel
            // Key the panel by id so its position/size survive a re-open…
            key={overlay.id}
            bounds="window"
            collapsible
            defaultPosition={defaultPositions[index]}
            defaultSize={definition.defaultSize}
            headerIcon={definition.headerIcon}
            headerIconLabel={definition.headerIconLabel}
            actions={
              <Button
                aria-label="Close"
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={() => onClose(overlay.id)}
              >
                <XIcon className="size-4" />
              </Button>
            }
          >
            {/* …but key the content by openId so each fresh open remounts the
                wizard, giving it a clean mount-time init from its payload. */}
            <Fragment key={overlay.openId}>{render(userId, overlay.payload)}</Fragment>
          </OverlayPanel>
        );
      })}
    </FloatingOverlayPortal>
  );
}
